import { IRemarkableBlockExtension } from '../../types';
import type { Remarkable } from 'remarkable';
import {
  MarkdownTableDefinition,
  MarkdownTableCell,
  MarkdownTableObjectCell,
} from './types';
import { CellParser } from './CellParser';
import { StateBlock, TagToken } from 'remarkable/lib';

function getCell(c: MarkdownTableCell): MarkdownTableObjectCell {
  return typeof c === 'string' ? { content: c } : c;
}

class TableToken {
  content: string;
  level = 1;
  type = name;
  constructor(
    content: MarkdownTableDefinition,
    public lines: [number, number],
  ) {
    this.content = JSON.stringify(content);
  }

  getTable() {
    const newTable = JSON.parse(this.content) as MarkdownTableDefinition;

    newTable.header = newTable.header.map((c) => getCell(c));
    newTable.rows = newTable.rows.map((row) =>
      row.map((cell) => getCell(cell)),
    );

    return newTable;
  }

  #getColumnAlignment(table: MarkdownTableDefinition, index: number) {
    let sum = 0,
      i = 0;
    for (i = 0; i < table.header.length && sum <= index; i++) {
      sum += Number.parseInt(getCell(table.header[i]).colSpan ?? '1', 10);
    }
    return getCell(table.header[i - 1]).align;
  }
  getTableString(parsers: CellParser[], md: Remarkable) {
    const table = this.getTable();

    const head =
      '<thead><tr>' +
      table.header
        .map((current) => {
          const cell = getCell(current);

          return `<th ${parsers
            .map((parser) => parser.getHtmlAttributes(current))
            .join(' ')}>${md.renderInline(cell.content)}</th>`;
        })
        .join('') +
      '</tr></thead>';

    const body =
      '<tbody>' +
      table.rows
        .map((row) => {
          return (
            '<tr>' +
            row
              .map((current, i) => {
                // eslint-disable-next-line no-param-reassign
                current.align = this.#getColumnAlignment(table, i);

                return `<td  ${parsers
                  .map((parser) => parser.getHtmlAttributes(current))
                  .join(' ')}>${md.renderInline(current.content)}</td>`;
              })
              .join('') +
            '</tr>'
          );
        })
        .join('\n') +
      '</tbody>';

    return `<table ${
      table.id ? `id="${table.id}"` : ''
    }>${head}${body}</table>`;
  }
}

const name = 'advancedTable';
/**
 * Identifica si una línea es una fila de cabecera válida
 */
const headerRowRegex = /^ *(?:\{ *#([\w\-]+) *\})? *((?:\|.+)+\|) *$/;
/**
 * Identifica si una línea es una fila válida
 */
const rowRegex = /^ *(?:\|.+)+\| *$/;
/**
 * Identifica si una línea es el separador entre header y body de la tabla
 */
const separatorRegex = /^ *(?:\| *:? *----* *:? *)+\| *$/;
/**
 * Identifica los elementos de una celda
 */

/**
 * How a table looks like:
 * 
| (.class) Cell1 | Cell2 | Cell3 | **_Cell4_** | Cell5 |
|-------|-------|:-----:|------------:|-------|
| (.class) CellContent |       |       |             |       |
 */

/**
 * Esta extensión permite implementar tablas más complejas en markdown
 */
export class TableExtension implements IRemarkableBlockExtension {
  #cellParsers: CellParser[] = [
    {
      /** className */
      getHtmlAttributes(cell) {
        return `${cell.className ? ` class="${cell.className ?? ''}"` : ''}`;
      },
      parseCellString(content) {
        const classNameRegex = /\(\.([^\)]+)\)/;
        const className = content.match(classNameRegex)?.[1];

        return {
          parsedCell: { className },
          newContent: content.replace(classNameRegex, ''),
        };
      },
    },
    {
      /** rowSpan & colSpan */
      getHtmlAttributes(cell) {
        return [
          `${cell.rowSpan ? `rowspan="${cell.rowSpan}"` : ''}`,
          `${cell.colSpan ? `colspan="${cell.colSpan}"` : ''}`,
        ].join(' ');
      },
      parseCellString(content) {
        const rowSpanRegex = /\(\{rowSpan:(\d+)\}\)/;
        const rowSpan = content.match(rowSpanRegex)?.[1];
        const colSpanRegex = /\(\{colSpan:(\d+)\}\)/;
        const colSpan = content.match(colSpanRegex)?.[1];

        return {
          parsedCell: {
            rowSpan,
            colSpan,
          },
          newContent: content
            .replace(rowSpanRegex, '')
            .replace(colSpanRegex, ''),
        };
      },
    },
    {
      /** styles */
      getHtmlAttributes(cell) {
        const styles = [];

        if (cell.align) styles.push(`text-align: ${cell.align}`);
        if (cell.style) styles.push(cell.style);

        const style = styles.join('; ');

        return `${style ? ` style="${style}"` : ''}`;
      },
      parseCellString(content) {
        const stylesRegex = /\(\{style:([^\}]+)\}\)/;
        const style = content.match(stylesRegex)?.[1] ?? '';

        return {
          parsedCell: { style },
          newContent: content.replace(stylesRegex, ''),
        };
      },
    },
  ];
  #parseCell(content: string) {
    const cell: MarkdownTableCell = { content };
    this.#cellParsers.forEach((current) => {
      const result = current.parseCellString(cell.content);
      Object.assign(cell, result.parsedCell, { content: result.newContent });
    });
    return cell;
  }

  #parseHeadRow(line: string) {
    const result = line.match(headerRowRegex);
    const id = result![1] ?? '';
    const content = result![2]!;

    return {
      id,
      content: content
        .split(/ *\| */)
        .slice(1, -1)
        .map((current) => {
          return this.#parseCell(current);
        }),
    };
  }

  #parseRow(content: string) {
    return content
      .split(/ *\| */)
      .slice(1, -1)
      .map((current) => {
        return this.#parseCell(current);
      });
  }

  name = name;
  parser = (
    state: StateBlock,
    startLine: number,
    endLine: number,
    checkMode: boolean,
  ): boolean => {
    if (startLine + 1 >= endLine) {
      return false;
    }

    const firstLineIsHeader = !!state
      .getLines(startLine, startLine + 1, 0, false)
      .match(headerRowRegex);

    if (checkMode) {
      return firstLineIsHeader;
    }

    if (firstLineIsHeader) {
      const headLine = state.getLines(startLine, startLine + 1, 0, false);
      const separatorLine = state.getLines(
        startLine + 1,
        startLine + 2,
        0,
        false,
      );

      if (!separatorLine.match(separatorRegex)) return false;

      try {
        const parsedHead = this.#parseHeadRow(headLine);

        const alignments = separatorLine.split(/ *\| */).filter(Boolean);

        alignments.map((current, i) => {
          if (current.match(/: *----* *:/)) {
            parsedHead.content[i].align = 'center';
          } else if (current.match(/----* *:/)) {
            parsedHead.content[i].align = 'right';
          }
        });

        const rows: MarkdownTableObjectCell[][] = [];
        let i = startLine + 2;
        let line: string | null = null;
        while ((line = state.getLines(i, i + 1, 0, false))) {
          if (line.match(rowRegex)) {
            const rowCells = this.#parseRow(line);
            line = state.getLines(++i, i + 1, 0, false);
            rows.push(rowCells);
          } else {
            break;
          }
        }

        state.tokens.push(
          new TableToken(
            { id: parsedHead.id, header: parsedHead.content, rows },
            [startLine, startLine + 2 + rows.length],
          ),
        );
        state.line += 2 + rows.length;

        return true;
      } catch (e) {
        console.error(e);
        return false;
      }
    }

    return false;
  };
  replacer = (md: Remarkable) => (tokens: TagToken[], idx: number) => {
    return (tokens[idx] as TableToken).getTableString(this.#cellParsers, md);
  };
  styles = () => '';
}
